import { isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { map, Observable, shareReplay, Subscriber, timeout } from 'rxjs';

import { EcmDispatcherModule, EnvUtils, StoreService } from '@ecm/dispatcher';

import { HttpMethod } from '../enums/http-method.enum';
import { RequestOptions, ReturnType } from '../enums/request-options';
import { Status } from '../enums/status.enum';
import { RequestUtils } from '../utils/http/request.utils';
import { IdUtils } from '../utils/id.utils';
import { BcoService } from './bco.service';
import { ErrorHandlerService } from './error-handler.service';
import { RandomUrlService } from './random-url.service';
import { UaService } from './ua-service';

@Injectable({
    providedIn: 'root',
})
export class ErableHttpService {
    /**
     * Class name for logger
     * @type {string}
     */
    private static CLASS_NAME = 'ErableHttpService';

    /**
     * Timeout multiplier factor used to modulate it in Eclair Mode (longer with the RP)
     * @type {number}
     */
    private static readonly ECLAIR_TIMEOUT_RATIO: number = 1.5;

    /**
     * Default request headers
     */
    private defaultHeaders: HttpHeaders;

    /**
     * Array used to store all the responses when in 2xx http code
     * The url called is used as a key
     * @type {Array<Observable<any>>}
     */
    private cache: Array<Observable<HttpResponse<any>>>;

    /**
     * Don't cache responses with these Response Type (Content-Type) values
     * @type {string[]}
     */
    private noCacheResponseTypesList: string[] = ['blob'];

    /**
     * Orange Headers for monitoring
     * @type {string}
     */
    private readonly ORIGIN_ID_HEADER: string = 'X-Orange-Origin-Id';
    private readonly CALLER_ID_HEADER: string = 'X-Orange-Caller-Id';
    private readonly DEVICE_TYPE_HEADER: string = 'X-App-Device-Type';

    /**
     * Defaults timeout for GET, POST and PUT method
     * @type {Object}
     */
    private readonly DEFAULT_TIMEOUT = {
        get: 10000,
        post: 10000,
        put: 10000,
        patch: 10000,
        delete: 10000,
    };

    /**
     * Error name for timeout
     * @type {string}
     */
    private readonly TIMEOUT_ERROR: string = 'TimeoutError';

    /**
     * Extend the angular/http constructor to add HTTP Headers needed by Erable
     * Be careful, they will be overwritten if you already set it in the RequestOption
     */
    public constructor(
        private httpClient: HttpClient,
        private bcoService: BcoService,
        private uaService: UaService,
        private errorHandlerService: ErrorHandlerService,
        @Inject(PLATFORM_ID) protected platformId,
        private randomUrlService: RandomUrlService,
    ) {
        this.initSessionId();

        // Set default headers
        // Be careful, HttpHeaders.set return a new instance !! so always chain call.
        this.defaultHeaders = new HttpHeaders()
            .set(this.ORIGIN_ID_HEADER, RequestUtils.getOriginId())
            .set(this.CALLER_ID_HEADER, RequestUtils.getCallerId())
            .set(this.DEVICE_TYPE_HEADER, this.uaService.getDeviceType())
            .set('Accept', 'application/json')
            .set('Content-Type', 'application/json')
            .set('Cache-Control', 'no-cache, no-store');

        // init local caches
        if (typeof this.cache === 'undefined') {
            this.cache = [];
        }
    }

    /**
     * GET method
     * use local cache if XHR to requested service url has been previously performed
     * else performs XHR to service url with HttpClient.get API and stores Response in cache
     * @param {string} url Service endpoint
     * @param {RequestOptions} options Request options
     * @return {Observable<HttpResponse<any>|any>} API response Observable (body response)
     */
    public get(url: string, options?: RequestOptions): Observable<HttpResponse<any> | any> {
        // if force, clear cache to perform request to service
        if (options?.force) {
            this.clear(url);
        }

        // service response for requested url already in cache
        if (typeof this.cache[url] !== 'undefined') {
            EcmDispatcherModule.sendConsoleInfo(
                ErableHttpService.CLASS_NAME,
                'Response from cached or pending Observable for url ' + url,
            );
            return this.processResponse(this.cache[url], options);
        }

        // no service response in cache for requested url
        EcmDispatcherModule.sendConsoleInfo(
            ErableHttpService.CLASS_NAME,
            'No response cache for ' + url + ', will perform XHR to service',
        );

        const obs: Observable<any> = new Observable((observer: Subscriber<any>) => {
            const { url: processedUrl, partner } = this.randomUrlService.getUrlAndPartner(
                url,
                options?.partner || 'undefined',
            );
            const computedOptions = { ...options, partner };

            const { requestOptions, transactionId } = RequestUtils.prepareRequest(
                HttpMethod.get,
                computedOptions,
                this.defaultHeaders,
                this.bcoService,
            );

            this.httpClient
                .get(processedUrl, requestOptions)
                .pipe(timeout(this.getTimeoutForMethod(HttpMethod.get, computedOptions)))
                .subscribe({
                    next: this.onNext.bind(this, observer, transactionId),
                    error: this.onError.bind(this, observer, transactionId),
                });
        }).pipe(shareReplay());

        // If the response type is not cachable, service response is not cached
        if (this.noCacheResponseTypesList.indexOf(RequestUtils.getResponseType(HttpMethod.get, options)) === -1) {
            this.cache[url] = obs;
        }

        return this.processResponse(obs, options);
    }

    /**
     * POST method
     * no local cache management
     * always performs XHR to service url with HttpClient.post API
     * @param {string} url Service endpoint
     * @param {any} body Request body
     * @param {RequestOptions} options Request options
     * @return {Observable<HttpResponse<any>|any>} Service response
     */
    public post(url: string, body: any, options?: RequestOptions): Observable<HttpResponse<any> | any> {
        const obs: Observable<any> = new Observable((observer: Subscriber<any>) => {
            const { url: processedUrl, partner } = this.randomUrlService.getUrlAndPartner(
                url,
                options.partner || 'undefined',
            );
            const computedOptions = { ...options, partner };

            const { requestOptions, transactionId } = RequestUtils.prepareRequest(
                HttpMethod.post,
                computedOptions,
                this.defaultHeaders,
                this.bcoService,
            );

            this.httpClient
                .post(processedUrl, body, requestOptions)
                .pipe(timeout(this.getTimeoutForMethod(HttpMethod.post, computedOptions)))
                .subscribe({
                    next: this.onNext.bind(this, observer, transactionId),
                    error: this.onError.bind(this, observer, transactionId),
                });
        });

        return this.processResponse(obs, options);
    }

    /**
     * PUT method
     * no local cache management
     * always performs XHR to service url with HttpClient.put API
     * @param {string} url Service endpoint
     * @param {any} body Request body
     * @param {RequestOptions} options Request options
     * @return {Observable<HttpResponse<any>|any>} Service response
     */
    public put(url: string, body: any, options?: RequestOptions): Observable<HttpResponse<any> | any> {
        const obs: Observable<any> = new Observable((observer: Subscriber<any>) => {
            const { url: processedUrl, partner } = this.randomUrlService.getUrlAndPartner(
                url,
                options.partner || 'undefined',
            );
            const computedOptions = { ...options, partner };

            const { requestOptions, transactionId } = RequestUtils.prepareRequest(
                HttpMethod.put,
                computedOptions,
                this.defaultHeaders,
                this.bcoService,
            );

            this.httpClient
                .put(processedUrl, body, requestOptions)
                .pipe(timeout(this.getTimeoutForMethod(HttpMethod.put, computedOptions)))
                .subscribe({
                    next: this.onNext.bind(this, observer, transactionId),
                    error: this.onError.bind(this, observer, transactionId),
                });
        });

        return this.processResponse(obs, options);
    }

    /**
     * PATCH method
     * no local cache management
     * always performs XHR to service url with HttpClient.patch API
     * @param {string} url Service endpoint
     * @param {any} body Request body
     * @param {RequestOptions} options Request options
     * @return {Observable<HttpResponse<any>|any>} Service response
     */
    public patch(url: string, body: any, options?: RequestOptions): Observable<HttpResponse<any> | any> {
        const obs: Observable<any> = new Observable((observer: Subscriber<any>) => {
            const { url: processedUrl, partner } = this.randomUrlService.getUrlAndPartner(
                url,
                options.partner || 'undefined',
            );
            const computedOptions = { ...options, partner };

            const { requestOptions, transactionId } = RequestUtils.prepareRequest(
                HttpMethod.patch,
                computedOptions,
                this.defaultHeaders,
                this.bcoService,
            );

            this.httpClient
                .patch(processedUrl, body, requestOptions)
                .pipe(timeout(this.getTimeoutForMethod(HttpMethod.patch, computedOptions)))
                .subscribe({
                    next: this.onNext.bind(this, observer, transactionId),
                    error: this.onError.bind(this, observer, transactionId),
                });
        });

        return this.processResponse(obs, options);
    }

    /**
     * DELETE method
     * no local cache management
     * always performs XHR to service url with HttpClient.delete API
     * @param {string} url Service endpoint
     * @param {RequestOptions} options Request options
     * @return {Observable<HttpResponse<any>|any>} Service response
     */
    public delete(url: string, options?: RequestOptions): Observable<HttpResponse<any> | any> {
        const obs: Observable<any> = new Observable((observer: Subscriber<any>) => {
            const { url: processedUrl, partner } = this.randomUrlService.getUrlAndPartner(
                url,
                options.partner || 'undefined',
            );
            const computedOptions = { ...options, partner };

            const { requestOptions, transactionId } = RequestUtils.prepareRequest(
                HttpMethod.delete,
                computedOptions,
                this.defaultHeaders,
                this.bcoService,
            );

            this.httpClient
                .delete(processedUrl, requestOptions)
                .pipe(timeout(this.getTimeoutForMethod(HttpMethod.delete, computedOptions)))
                .subscribe({
                    next: this.onNext.bind(this, observer, transactionId),
                    error: this.onError.bind(this, observer, transactionId),
                });
        });

        return this.processResponse(obs, options);
    }

    /**
     * Deletes cache entry
     * @param {string} url
     * @returns {boolean} true if delete ok (will be true even if the key did not exists)
     */
    public clear(url: string): boolean {
        return delete this.cache[url];
    }

    /**
     * Check if a JWT token is present and get the advisor datas from it to store them in the StoreService
     * This token is added in response headers by the Reverse Proxy on the necFE application for Eclair Context
     * The datas stored are used by the Kenobi Service to send Advisor information to StatProcess
     * @param {HttpHeaders} headers
     */
    private computeAdvisorJwtHeader(headers: HttpHeaders) {
        if (!EnvUtils.isEclair()) {
            return;
        }
        try {
            if (headers.has('Authorization')) {
                const authHeaders = headers.getAll('Authorization');
                const token = authHeaders[0].split('.')[1];

                // We get the JWT from a trusted server, we don't validate the signature
                const advisorTokenData = JSON.parse(atob(token));

                const advisor = {
                    alliance: advisorTokenData.alliance,
                    basicat: advisorTokenData.basicat,
                    team: advisorTokenData.team,
                    activityteam: advisorTokenData.activityteam,
                    cuid: advisorTokenData.cuid,
                    role: advisorTokenData.role,
                };

                StoreService.set('advisor', advisor);
            }
        } catch (e) {
            // if an error occurs we don't stop treatment, Token is not mandatory
            EcmDispatcherModule.sendConsoleError(ErableHttpService.CLASS_NAME, 'An error occured in JWT decoding');
        }
    }

    /**
     * Function to call on httpClient next callback
     * @param {Subscriber<any>} observer
     * @param {string} transactionId
     * @param {HttpResponse<any>} response
     */
    private onNext(observer: Subscriber<any>, transactionId: string, response: HttpResponse<any>): void {
        this.computeAdvisorJwtHeader(response.headers);

        // log transaction end in BCOService
        this.bcoService.endTransaction(transactionId, Status.OK, response.status.toString(), '');

        // complete observer
        observer.next(response);
        observer.complete();
    }

    private onError(observer: Subscriber<any>, transactionId: string, httpError: HttpErrorResponse): void {
        EcmDispatcherModule.sendConsoleError(ErableHttpService.CLASS_NAME, httpError.message);
        const timeOut: boolean = httpError.name === this.TIMEOUT_ERROR;
        let errorCode: string = timeOut ? 'ECM_TO' : httpError.status?.toString();
        const status = this.getErrorStatus(timeOut, errorCode);
        const errorMessage: string =
            httpError.error?.error?.technicalDescription ||
            httpError.error?.error?.technicalMessage ||
            httpError.message ||
            'undefined';

        // log error if it can't be determined
        if (errorCode === undefined) {
            this.errorHandlerService.handleError(httpError);
            errorCode = `ECM_${httpError.name}`;
        }
        this.bcoService.endTransaction(transactionId, status, errorCode, errorMessage);
        // throw Observable error
        observer.error(httpError);
    }

    private getErrorStatus(timeOut: boolean, errorCode: string) {
        if (timeOut) {
            return Status.TO;
        } else if (errorCode === undefined) {
            return Status.KORG;
        } else {
            return errorCode.startsWith('4') ? Status.KORG : Status.KOSI;
        }
    }

    /**
     * Process the response to return the HttpResponse or the body, based on the options parameter
     * The returnType to httpResponse is usefull when you need to get headers or HTTP Code Status
     * @param observable
     * @param {RequestOptions} options
     * @returns {Observable<HttpResponse<any>|any>}
     */
    private processResponse(
        observable: Observable<HttpResponse<any> | any>,
        options?: RequestOptions,
    ): Observable<HttpResponse<any> | any> {
        if (options?.returnType === ReturnType.HTTP_RESPONSE) {
            return observable;
        } else {
            return observable.pipe(map((response) => response.body));
        }
    }

    /**
     * Initializes and stores session data
     */
    private initSessionId(): void {
        if (isPlatformBrowser(this.platformId)) {
            const sessionId: string = IdUtils.generateUUID();
            const session = {
                id: sessionId,
            };
            EcmDispatcherModule.sendConsoleInfo(ErableHttpService.CLASS_NAME, 'Storing Session ' + sessionId);
            StoreService.set('session', session);
        }
    }

    /**
     * function used to get the configuration for the timeout operator depending on config and environment
     * @param {HttpMethod} method
     * @param {{timeout:number}} options
     */
    private getTimeoutForMethod(method: HttpMethod, options: { timeout?: number }): number {
        const configTimeout: number = options.timeout ? options.timeout : this.DEFAULT_TIMEOUT[method.toLowerCase()];
        const ratio: number = EnvUtils.isEclair() ? ErableHttpService.ECLAIR_TIMEOUT_RATIO : 1;

        return ratio * configTimeout;
    }
}
